import { isITask, ITask } from '../../components/task/Task'
import Dispatcher, { IAction, IActionPayload } from '../Dispatcher'

/**
 * Interface for action payloads when the action
 * is to start the flow to create a new task for a given swimlane
 */
export interface ITaskCreatePayload {
    boardId: number
    swimlaneId: number
}

/**
 * Used for Add, Update and Delete Task actions
 */
export interface ITaskPayload extends ITaskCreatePayload {
    Task: ITask
}

/**
 * Used when requesting a Task to be moved.
 * May move a task from anywhere to anywhere.
 */
export interface ITaskMovePayload {
    from: { boardId: number; swimlaneId: number; taskId: number }
    to: { boardId: number; swimlaneId: number; taskId: number }
}

// Types associated with TASK Actions
const ADD_TASK = 'ADD_TASK'
const UPDATE_TASK = 'UPDATE_TASK'
const DELETE_TASK = 'DELETE_TASK'
const CREATE_TASK = 'CREATE_TASK'
const MOVE_TASK = 'MOVE_TASK'

/**
 * Typeguard for ITaskMovePayload
 * If the action payload contains from and to properties that are fully populated then true.
 * Else false
 * @param action Action to check
 */
function isTaskMovePayloadAction(
    action: IAction | IActionPayload<ITaskMovePayload>
): action is IActionPayload<ITaskMovePayload> {
    const payload = action as IActionPayload<ITaskMovePayload>
    // ensure we have a payload
    if (!payload.payload) return false
    // ensure we have from and to
    const { from, to } = payload.payload
    if (from === undefined || to === undefined) return false
    // ensure all properties are defined for both
    if (
        from.boardId === undefined ||
        from.swimlaneId === undefined ||
        from.taskId === undefined ||
        to.boardId === undefined ||
        to.swimlaneId === undefined ||
        to.taskId === undefined
    )
        return false
    return true
}

/**
 * Typeguard for ITaskCreatePayload
 * If the action payload contains board and swimlane properties allow it through.
 * else return true indicating it's not an ITaskCreatePayload action
 * @param action Action to check
 */
function isTaskCreatePayloadAction(
    action: IAction | IActionPayload<ITaskCreatePayload>
): action is IActionPayload<ITaskCreatePayload> {
    const payload = action as IActionPayload<ITaskCreatePayload>
    // ensure we have a payload
    if (!payload.payload) return false
    // ensure we have a board id
    if (payload.payload.boardId === undefined) return false
    // ensure we have a swimlane id
    if (payload.payload.swimlaneId === undefined) return false
    return true
}

/**
 * Typeguard for ITaskPayload Action.
 * Returns undefined if the action passed in is not a ITaskPayload.
 * Otherwise will return true.
 * @param action Action to check
 */
function isTaskPayloadAction(
    action: IAction | IActionPayload<ITaskPayload>
): action is IActionPayload<ITaskPayload> {
    if (!isTaskCreatePayloadAction(action)) return false
    const payload = action as IActionPayload<ITaskPayload>
    // ensure we have a task
    if (!payload.payload.Task) return false
    // ensure task is valid
    if (!isITask(payload.payload.Task)) return false
    return true
}

/**
 * Dispatches action to add a Task to a swimlane in a board
 * @param boardId ID of board with swimlane to add task to
 * @param swimlaneId ID of swimlane to add Task to
 * @param Task Task to add
 */
function addTask(boardId: number, swimlaneId: number, Task: ITask) {
    Dispatcher.dispatch({
        actionType: ADD_TASK,
        payload: {
            boardId: boardId,
            swimlaneId: swimlaneId,
            Task: Task,
        },
    } as IActionPayload<ITaskPayload>)
}

/**
 * Dispatches action to update a Task
 * @param boardId ID of board with swimlane holding the task
 * @param swimlaneId ID of swimlane holding the task
 * @param Task Updated Task
 */
function updateTask(boardId: number, swimlaneId: number, Task: ITask) {
    Dispatcher.dispatch({
        actionType: UPDATE_TASK,
        payload: {
            boardId: boardId,
            swimlaneId: swimlaneId,
            Task: Task,
        },
    } as IActionPayload<ITaskPayload>)
}

/**
 * Dispatches action to delete a Task
 * @param boardId ID of board to holding the swimlane this task belongs to
 * @param swimlaneId ID of swimlane holding this task
 * @param Task Task to delete
 */
function deleteTask(boardId: number, swimlaneId: number, Task: ITask) {
    Dispatcher.dispatch({
        actionType: DELETE_TASK,
        payload: {
            boardId: boardId,
            swimlaneId: swimlaneId,
            Task: Task,
        },
    } as IActionPayload<ITaskPayload>)
}

/**
 * Dispatches action to create a task
 * @param boardId Board this task will be ancestral to
 * @param swimlaneId Swimlane this board will belong to
 */
function createTask(boardId: number, swimlaneId: number) {
    Dispatcher.dispatch({
        actionType: CREATE_TASK,
        payload: {
            boardId,
            swimlaneId,
        },
    } as IActionPayload<ITaskCreatePayload>)
}

/**
 * Dispatches action to move a task between both swimlanes and boards
 * @param fromBoardId Board the task to move belongs to
 * @param fromSwimlaneId Swimlane the task to move belongs to
 * @param taskId ID of the task to be moved
 * @param toBoardId Board to move the task to
 * @param toSwimlaneId Swimlane to move the task to
 * @param toTaskId Optional task id to place fromTask at, else places at end of toSwimlane
 */
function moveTask(
    fromBoardId: number,
    fromSwimlaneId: number,
    taskId: number,
    toBoardId: number,
    toSwimlaneId: number,
    toTaskId: number = -1
) {
    Dispatcher.dispatch({
        actionType: MOVE_TASK,
        payload: {
            from: {
                boardId: fromBoardId,
                swimlaneId: fromSwimlaneId,
                taskId: taskId,
            },
            to: {
                boardId: toBoardId,
                swimlaneId: toSwimlaneId,
                taskId: toTaskId,
            },
        },
    } as IActionPayload<ITaskMovePayload>)
}

export {
    addTask,
    updateTask,
    deleteTask,
    createTask,
    moveTask,
    isTaskPayloadAction,
    isTaskCreatePayloadAction,
    isTaskMovePayloadAction,
    ADD_TASK,
    UPDATE_TASK,
    DELETE_TASK,
    CREATE_TASK,
    MOVE_TASK,
}
